import React, { useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import ReportPageShell from "../components/ReportPageShell";
import MetricGrid from "../components/MetricGrid";
import StatCard from "../components/StatCard";
import { SecondaryButton } from "../../../components/Button";
import { useEventSummary } from "../shared/useEventSummary";
import { useSearchGate } from "../shared/useSearchGate";
import { defaultFilters } from "../shared/filters";
import { centsToUsd, formatMetric, METRIC_LABELS } from "../shared/format";
import {
  avgTicketCents,
  buildEventSummaryCsv,
  eventSummaryCsvFilename,
} from "../shared/eventSummaryCsv";
import { MetricKey } from "../api/types";

const FINANCIAL_CARDS: MetricKey[] = [
  "NetSales",
  "Gross",
  "Tax",
  "Discount",
  "Refunds",
  "Tip",
  "Surcharge",
  "OrderCount",
  "TransactionCount",
];

const TOOLTIP =
  "Event Summary (RP-2146/2171) — the event's full financial set, Net-by-hour, and top products in one " +
  "view. Net Sales is the real-money net that matches the Financial reports. Pick a single event.";

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="mb-4 rounded-md bg-white p-4 shadow-sm">
    <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-gray-700">{title}</h3>
    {children}
  </div>
);

// Event Summary dashboard. NOTE (follow-on): Marc eventually wants this as the login splash page with
// configurable widgets — this report-page version is the interim; the splash is NOT built here.
export default function EventSummaryDashboard() {
  const [filters, setFilters] = useState(defaultFilters);
  const { submitted, isDirty, hasRun, search } = useSearchGate({ filters });
  const es = useEventSummary(submitted ? submitted.filters : null);

  const handleExportCsv = () => {
    if (!es.summary) return;
    const t = es.summary.totals;
    const cards = FINANCIAL_CARDS.filter((m) => es.summary!.metrics.includes(m));
    const csv = buildEventSummaryCsv(
      es.summary,
      cards,
      avgTicketCents(t.TransactionCount ?? 0, t.Gross ?? 0)
    );
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = eventSummaryCsvFilename(es.summary);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <ReportPageShell
      title="Event Summary"
      tooltipText={TOOLTIP}
      filters={filters}
      onFiltersChange={setFilters}
      state={{ data: es.summary, loading: es.loading, error: es.error }}
      idlePrompt="Select a single event to see its summary."
      onSearch={search}
      isDirty={isDirty}
      hasRun={hasRun}
      actions={
        <SecondaryButton onClick={handleExportCsv} isDisabled={!es.summary || es.loading}>
          Export summary (CSV)
        </SecondaryButton>
      }
    >
      {(summary) => {
        const hourlyData = (es.hourly?.rows ?? []).map((r) => ({
          label: r.label,
          net: r.values.NetSales ?? 0,
        }));

        return (
          <>
            <Section title="Financial summary">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                {FINANCIAL_CARDS.filter((m) => summary.metrics.includes(m)).map((m) => (
                  <StatCard
                    key={m}
                    label={METRIC_LABELS[m]}
                    value={formatMetric(m, summary.totals[m])}
                    emphasis={m === "NetSales"}
                  />
                ))}
                {/* Avg Ticket — legacy Event Summary stat (Gross ÷ Transactions); see avgTicketCents. */}
                <StatCard
                  label="Avg Ticket"
                  value={centsToUsd(
                    avgTicketCents(
                      summary.totals.TransactionCount ?? 0,
                      summary.totals.Gross ?? 0
                    )
                  )}
                />
              </div>
            </Section>

            {es.hourly && (
              <Section title="Net Sales by hour">
                <div className="overflow-x-auto">
                  <LineChart
                    width={Math.max(720, hourlyData.length * 46)}
                    height={300}
                    data={hourlyData}
                    margin={{ top: 16, right: 16, left: 10, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: "0.75rem", fontWeight: 600, fill: "#4a5568" }}
                    />
                    <YAxis
                      width={90}
                      tickFormatter={(v: number) => centsToUsd(v)}
                      tick={{ fontSize: "0.75rem", fontWeight: 600, fill: "#4a5568" }}
                    />
                    <Tooltip formatter={(v: number) => centsToUsd(v)} />
                    <Line type="monotone" dataKey="net" name="Net Sales" stroke="#B71C1C" strokeWidth={2} dot={false} />
                  </LineChart>
                </div>
              </Section>
            )}

            {es.topItems && (
              <Section title="Top products">
                <MetricGrid
                  data={es.topItems}
                  keyColumnHeader="Item"
                  exportName="EventSummary_TopItems"
                  height="420px"
                />
              </Section>
            )}
          </>
        );
      }}
    </ReportPageShell>
  );
}
